import styled, { css } from 'styled-components';

import { theme } from 'coreOld/themes';
import { ReactNode } from 'react';

const getBorderColor = (hasBorder = true) => {
  if (!hasBorder) {
    return { border: `1px solid ${theme.palette.neutral.light}` };
  }

  return {
    border: `1px solid ${theme.palette.borderColor}`,
  };
};

const getHoverBorderColor = (hasBorder = true) => {
  if (!hasBorder) {
    return { border: `1px solid ${theme.palette.hover.main}` };
  }

  return {
    border: `1px solid ${theme.palette.hover.borderColor}`,
  };
};

const getFontStyle = (fontInherit: boolean, fontSize?: number | string) => {
  if (fontInherit) {
    return {
      fontSize: 'inherit',
    };
  }

  return {
    fontSize: fontSize || 12,
  };
};

interface DateInputProps {
  selectsRange?: boolean;
  size?: string;
  hasBorder?: boolean;
  fontInherit?: boolean;
  fontSize?: number;
  isDisabledState?: boolean;
  showBorder?: boolean;
  children?: ReactNode;
}

const DateInput = styled.div<DateInputProps>`
  position: relative;
  margin-left: -1px;
  border-left: ${({ theme, selectsRange = true }) =>
    selectsRange && `1px solid ${theme.palette.neutral.lightest}`};
  font-size: inherit;

  .react-datepicker {
    border: none;
    border-radius: 0px;
  }

  .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::after {
    border-bottom-color: hsl(0, 0%, 100%);
  }

  .react-datepicker__input-container input {
    width: 100%;
    align-items: center;
    background-color: hsl(0, 0%, 100%);
    border-color: ${({ theme }) => theme.palette.borderColor};
    border-radius: 0;
    border-style: solid;
    border-width: 1px;
    box-shadow: none;
    cursor: default;
    display: flex;
    justify-content: space-between;
    min-height: ${({ theme, size }) => theme.inputHeight[size] || '45px'};
    outline: 0;
    position: relative;
    transition: all 100ms;
    box-sizing: border-box;
    padding: 3px 10px;
    cursor: pointer;
    color: inherit;

    ${({ hasBorder }) => {
      return { ...getBorderColor(hasBorder) };
    }}

    ${({ fontInherit, fontSize }) => {
      return { ...getFontStyle(fontInherit, fontSize) };
    }}

    &:focus,&:hover {
      box-shadow: none;

      ${({ hasBorder }) => {
        return { ...getHoverBorderColor(hasBorder) };
      }}
    }

    ${({ isDisabledState }) =>
      isDisabledState &&
      css`
        border: none;
        cursor: not-allowed;
        font-size: inherit;
        opacity: 0.5;

        &:focus,
        &:hover {
          border: none;
        }
      `}
  }

  .react-datepicker__current-month {
    font:
      600 12px 'arial',
      sans-serif;
    padding-bottom: 5px;
    padding-top: 5px;
    color: ${({ theme }) => theme.palette.neutral.dark};
  }

  .react-datepicker__day-name {
    font-weight: 600;
  }

  .react-datepicker__header {
    font:
      12px 'arial',
      sans-serif;
    border: none;
    font-weight: 500;
    background-color: transparent;
  }

  .react-datepicker__month {
    margin-top: 0;
    margin: 0;
    font:
      12px 'arial',
      sans-serif;
  }

  .react-datepicker__week {
    margin-top: -3px;
    font-weight: 400;
  }

  .react-datepicker__day--in-range {
    border-radius: 0px;
    background-color: ${({ theme }) => theme.palette.backgrounds.range};
    border: none;
    color: #000;
  }

  .react-datepicker__day--in-selecting-range {
    background-color: ${({ theme }) => theme.palette.backgrounds.range};
    border-radius: 0;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .react-datepicker__day--in-selecting-range.react-datepicker__day--selected {
    background-color: ${({ theme }) => theme.palette.primary.dark};
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .react-datepicker__day {
    margin: 0;
    margin-bottom: 3px;
    width: 30px;
    padding: 1px 3px;
  }

  .react-datepicker__day--selected {
    background-color: ${({ theme }) => theme.palette.primary.dark};
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .react-datepicker__day--range-start {
    background-color: ${({ theme }) => theme.palette.primary.dark};
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .react-datepicker__day--range-end,
  .react-datepicker__day--selected:last-of-type {
    background-color: ${({ theme }) => theme.palette.primary.dark};
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .react-datepicker__day--outside-month {
    visibility: hidden;
  }

  ${({ showBorder }) =>
    showBorder &&
    css`
      .react-datepicker {
        border: ${({ theme }) => `1px solid ${theme.palette.neutral.lightest}`};
      }
    `}

  .react-datepicker__year-dropdown-container--select, 
  .react-datepicker__month-dropdown-container--select, 
  .react-datepicker__month-year-dropdown-container--select, 
  .react-datepicker__year-dropdown-container--scroll, 
  .react-datepicker__month-dropdown-container--scroll, 
  .react-datepicker__month-year-dropdown-container--scroll {
    display: inline-block;
    margin: 0 12px;
  }

  .react-datepicker__close-icon::after {
    background-color: inherit;
    color: inherit;
    font-size: 24px;
    padding: 4px;
  }
`;

export default DateInput;
